import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { TokenData } from '../hooks/useBondingCurve';
import { ERC20_ABI, USDT_ADDRESS } from '../config/contracts';
import { fetchUserCosts } from '../lib/supabase';

interface PortfolioPageProps {
  account: string | null;
  tokens: TokenData[];
  provider: ethers.providers.Web3Provider | null;
  onSelectToken: (token: TokenData) => void;
}

interface HoldingItem {
  token: TokenData;
  balance: string;
  value: string;
}

const PortfolioPage: React.FC<PortfolioPageProps> = ({
  account,
  tokens,
  provider,
  onSelectToken,
}) => {
  const [holdings, setHoldings] = useState<HoldingItem[]>([]);
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [bnbBalance, setBnbBalance] = useState('0');
  const [loading, setLoading] = useState(false);
  const [totalValue, setTotalValue] = useState('0');
  const [totalCost, setTotalCost] = useState('0');       // 持仓本金
  const [totalPnl, setTotalPnl] = useState('0');         // 综合盈亏
  const [showPnlDetail, setShowPnlDetail] = useState(false); // 是否展示盈亏明细
  const [costMap, setCostMap] = useState<Record<string, number>>({}); // 每个代币本金 map

  useEffect(() => {
    if (!account || !provider || tokens.length === 0) return;

    const fetchBalances = async () => {
      setLoading(true);
      try {
        // BNB balance
        const bnbBal = await provider.getBalance(account);
        setBnbBalance(parseFloat(ethers.utils.formatEther(bnbBal)).toFixed(4));

        // USDT balance
        const usdtContract = new ethers.Contract(USDT_ADDRESS, ERC20_ABI, provider);
        const usdtBal = await usdtContract.balanceOf(account);
        setUsdtBalance(parseFloat(ethers.utils.formatUnits(usdtBal, 18)).toFixed(2));

        // Token balances
        const items: HoldingItem[] = [];
        for (const token of tokens) {
          const contract = new ethers.Contract(token.tokenAddress, ERC20_ABI, provider);
          const bal = await contract.balanceOf(account);
          if (bal.gt(0)) {
            const balFormatted = parseFloat(ethers.utils.formatUnits(bal, token.decimals)).toFixed(4);
            const price = parseFloat(token.currentPrice);
            const valueUSDT = (parseFloat(balFormatted) * price).toFixed(2);
            items.push({
              token,
              balance: balFormatted,
              value: valueUSDT,
            });
          }
        }
        setHoldings(items);

        const total = items.reduce((s, h) => s + parseFloat(h.value), 0);
        setTotalValue(total.toFixed(2));

        // 加载用户成本（本金）
        const costs = await fetchUserCosts(account).catch(() => ({}));
        setCostMap(costs);
        // 只统计有持仓的代币的本金
        const activeCost = items.reduce((s, h) => {
          const addr = h.token.tokenAddress.toLowerCase();
          return s + Math.max(0, costs[addr] || 0);
        }, 0);
        setTotalCost(activeCost.toFixed(2));
        setTotalPnl((total - activeCost).toFixed(2));
      } catch (e) {
        console.error('Portfolio fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();
  }, [account, tokens, provider]);

  if (!account) {
    return (
      <div style={styles.noWallet}>
        <div style={styles.noWalletIcon}>👛</div>
        <div style={styles.noWalletTitle}>连接钱包查看持仓</div>
        <div style={styles.noWalletSub}>连接 MetaMask 或其他兼容钱包以查看你的代币持仓</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.pageTitle}>我的持仓</div>

      {/* Wallet Overview */}
      <div style={styles.overviewGrid}>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>💰</div>
          <div>
            <div style={styles.overviewLabel}>BNB 余额</div>
            <div style={styles.overviewValue}>{bnbBalance} BNB</div>
          </div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>🟡</div>
          <div>
            <div style={styles.overviewLabel}>USDT 余额</div>
            <div style={{ ...styles.overviewValue, color: '#ffd600' }}>${usdtBalance}</div>
          </div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>📊</div>
          <div>
            <div style={styles.overviewLabel}>代币持仓价值</div>
            <div style={{ ...styles.overviewValue, color: '#00e676' }}>${totalValue}</div>
          </div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>💵</div>
          <div>
            <div style={styles.overviewLabel}>持仓本金投入</div>
            <div style={{ ...styles.overviewValue, color: '#a0b0c8' }}>${totalCost}</div>
          </div>
        </div>
        <div
          style={{ ...styles.overviewCard, cursor: 'pointer', border: '1px solid rgba(0,212,255,0.2)' }}
          onClick={() => setShowPnlDetail(v => !v)}
          title="点击查看盈亏明细"
        >
          <div style={styles.overviewIcon}>{parseFloat(totalPnl) >= 0 ? '📈' : '📉'}</div>
          <div>
            <div style={styles.overviewLabel}>综合盈亏 ▸</div>
            <div style={{
              ...styles.overviewValue,
              color: parseFloat(totalPnl) >= 0 ? '#00e676' : '#ff4d6d',
            }}>
              {parseFloat(totalPnl) >= 0 ? '+' : ''}${totalPnl}
            </div>
          </div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>🪙</div>
          <div>
            <div style={styles.overviewLabel}>持有代币种数</div>
            <div style={{ ...styles.overviewValue, color: '#7c4dff' }}>{holdings.length}</div>
          </div>
        </div>
      </div>

      {/* PnL Detail */}
      {showPnlDetail && (
        <div style={styles.pnlSection}>
          <div style={styles.pnlTitle}>盈亏明细</div>
          {holdings.length === 0 ? (
            <div style={styles.pnlEmpty}>暂无持仓记录</div>
          ) : (
            <div style={styles.pnlTable}>
              <div style={styles.pnlHeader}>
                <span>代币</span>
                <span style={{ textAlign: 'right' as const }}>本金</span>
                <span style={{ textAlign: 'right' as const }}>当前估值</span>
                <span style={{ textAlign: 'right' as const }}>盈亏</span>
              </div>
              {holdings.map(item => {
                const addr = item.token.tokenAddress.toLowerCase();
                const cost = Math.max(0, costMap[addr] || 0);
                const val = parseFloat(item.value);
                const pnl = val - cost;
                const pnlPct = cost > 0 ? ((pnl / cost) * 100) : null;
                const isUp = pnl >= 0;
                return (
                  <div key={addr} style={styles.pnlRow}>
                    <div style={styles.pnlTokenCell}>
                      <div style={styles.pnlTokenIcon}>
                        {item.token.symbol.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={styles.pnlTokenName}>{item.token.name}</div>
                        <div style={styles.pnlTokenSymbol}>${item.token.symbol}</div>
                      </div>
                    </div>
                    <div style={styles.pnlCell}>${cost.toFixed(2)}</div>
                    <div style={{ ...styles.pnlCell, color: '#00e676' }}>${item.value}</div>
                    <div style={{ ...styles.pnlCell, color: isUp ? '#00e676' : '#ff4d6d', fontWeight: 700 }}>
                      {isUp ? '+' : ''}${pnl.toFixed(2)}
                      {pnlPct !== null && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: isUp ? '#00e676' : '#ff4d6d' }}>
                          {isUp ? '+' : ''}{pnlPct.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Account Address */}
      <div style={styles.addrBox}>
        <span style={styles.addrLabel}>钱包地址</span>
        <span style={styles.addrValue}>{account}</span>
        <a
          href={`https://bscscan.com/address/${account}`}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.bscscanLink}
        >
          BSCScan ↗
        </a>
      </div>

      {/* Holdings Table */}
      <div style={styles.tableSection}>
        <div style={styles.tableTitleRow}>
          <div style={styles.tableTitle}>代币持仓明细</div>
          {loading && <span style={styles.loadingText}>加载中...</span>}
        </div>

        {loading ? (
          <div style={styles.tableLoading}>
            {[1, 2, 3].map(i => (
              <div key={i} style={styles.skeletonRow} />
            ))}
          </div>
        ) : holdings.length === 0 ? (
          <div style={styles.emptyHoldings}>
            <div style={styles.emptyHoldingsIcon}>📭</div>
            <div style={styles.emptyHoldingsText}>暂无持仓记录</div>
            <div style={styles.emptyHoldingsSub}>前往市场购买代币后，持仓将在此显示</div>
          </div>
        ) : (
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <span>代币</span>
              <span style={{ textAlign: 'right' as const }}>持有数量</span>
              <span style={{ textAlign: 'right' as const }}>估值</span>
              <span style={{ textAlign: 'right' as const }}>状态</span>
            </div>
            {holdings.map(item => (
              <div
                key={item.token.tokenAddress}
                style={styles.tableRow}
                onClick={() => onSelectToken(item.token)}
              >
                <div style={styles.tableTokenCell}>
                  <div style={styles.tableTokenIcon}>
                    {item.token.symbol.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={styles.tableTokenName}>{item.token.name}</div>
                    <div style={styles.tableTokenSymbol}>${item.token.symbol}</div>
                  </div>
                </div>
                <div style={styles.tableCell}>
                  {parseFloat(item.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                </div>
                <div style={{ ...styles.tableCell, color: '#00e676', fontWeight: 700 }}>
                  ${item.value}
                </div>
                <div style={styles.tableCell}>
                  {item.token.migrated ? (
                    <span style={styles.statusMigrated}>已迁移</span>
                  ) : (
                    <span style={styles.statusActive}>交易中</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transaction History Notice */}
      <div style={styles.historyNotice}>
        <span style={styles.historyIcon}>📋</span>
        <span>
          完整交易历史请前往{' '}
          <a
            href={`https://bscscan.com/address/${account}`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            BSCScan ↗
          </a>
          {' '}查看
        </span>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 800,
    color: '#e8eef7',
  },
  noWallet: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '80px 0',
    gap: 16,
  },
  noWalletIcon: { fontSize: 56 },
  noWalletTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#e8eef7',
  },
  noWalletSub: {
    fontSize: 14,
    color: '#6b7d99',
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
  },
  overviewCard: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  overviewIcon: {
    fontSize: 24,
    flexShrink: 0,
  },
  overviewLabel: {
    fontSize: 11,
    color: '#6b7d99',
    marginBottom: 4,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
  },
  overviewValue: {
    fontSize: 17,
    fontWeight: 800,
    color: '#e8eef7',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  addrBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    flexWrap: 'wrap' as const,
  },
  addrLabel: {
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  addrValue: {
    fontSize: 13,
    color: '#e8eef7',
    fontFamily: 'monospace',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  bscscanLink: {
    fontSize: 12,
    color: '#00d4ff',
    textDecoration: 'none',
    fontWeight: 600,
    flexShrink: 0,
  },
  tableSection: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    minWidth: 0,
  },
  tableTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #1e2d4a',
  },
  tableTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e8eef7',
  },
  loadingText: {
    fontSize: 13,
    color: '#6b7d99',
  },
  tableLoading: {
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  skeletonRow: {
    height: 56,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  emptyHoldings: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '60px 0',
    gap: 12,
  },
  emptyHoldingsIcon: { fontSize: 40 },
  emptyHoldingsText: {
    fontSize: 16,
    fontWeight: 600,
    color: '#e8eef7',
  },
  emptyHoldingsSub: {
    fontSize: 13,
    color: '#6b7d99',
  },
  table: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    padding: '12px 16px',
    fontSize: 11,
    color: '#3d5070',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: '1px solid #1e2d4a',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    padding: '14px 16px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  tableTokenCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  tableTokenIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #00d4ff, #7c4dff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
    color: '#fff',
    flexShrink: 0,
  },
  tableTokenName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#e8eef7',
  },
  tableTokenSymbol: {
    fontSize: 12,
    color: '#6b7d99',
    marginTop: 1,
  },
  tableCell: {
    fontSize: 14,
    color: '#e8eef7',
    textAlign: 'right' as const,
  },
  statusActive: {
    fontSize: 11,
    color: '#00e676',
    background: 'rgba(0,230,118,0.1)',
    border: '1px solid rgba(0,230,118,0.2)',
    borderRadius: 6,
    padding: '3px 8px',
    fontWeight: 600,
  },
  statusMigrated: {
    fontSize: 11,
    color: '#ffd600',
    background: 'rgba(255,214,0,0.1)',
    border: '1px solid rgba(255,214,0,0.2)',
    borderRadius: 6,
    padding: '3px 8px',
    fontWeight: 600,
  },
  tradeBtn: {
    background: 'rgba(0,212,255,0.1)',
    border: '1px solid rgba(0,212,255,0.3)',
    color: '#00d4ff',
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  historyNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 10,
    fontSize: 13,
    color: '#6b7d99',
  },
  historyIcon: { fontSize: 16 },
  link: {
    color: '#00d4ff',
    textDecoration: 'none',
    fontWeight: 600,
  },
  pnlSection: {
    background: '#131929',
    border: '1px solid rgba(0,212,255,0.2)',
    borderRadius: 16,
    overflow: 'hidden',
  },
  pnlTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e8eef7',
    padding: '16px 20px',
    borderBottom: '1px solid #1e2d4a',
  },
  pnlEmpty: {
    textAlign: 'center' as const,
    color: '#6b7d99',
    padding: '30px 0',
    fontSize: 13,
  },
  pnlTable: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  pnlHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    padding: '10px 16px',
    fontSize: 11,
    color: '#3d5070',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: '1px solid #1e2d4a',
  },
  pnlRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    padding: '12px 16px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
  },
  pnlTokenCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  pnlTokenIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #00d4ff, #7c4dff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 800,
    color: '#fff',
    flexShrink: 0,
  },
  pnlTokenName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#e8eef7',
  },
  pnlTokenSymbol: {
    fontSize: 11,
    color: '#6b7d99',
  },
  pnlCell: {
    fontSize: 13,
    color: '#a0b0c8',
    textAlign: 'right' as const,
  },
};

export default PortfolioPage;
